import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchJson } from '../api';
import CityGrid from '../components/CityGrid';
import { generateWebsiteSchema, generateOrganizationSchema, generateBreadcrumbSchema, generateSchemaGraph } from '../utils/schema';
import { generateCompleteSEO, generateLocationKeywords } from '../utils/seo';
import SEOHead from '../components/SEOHead';
import { getSiteBaseUrl } from '../config/site';

const StateSchema = ({ stateSlug, stateName, cities }) => {
  const baseUrl = getSiteBaseUrl();
  const pageUrl = `${baseUrl}/in/${stateSlug}`;
  
  const schema = generateSchemaGraph([
    generateWebsiteSchema(),
    generateOrganizationSchema(),
    generateBreadcrumbSchema([
      { name: 'Home', url: baseUrl },
      { name: stateName, url: pageUrl }
    ]),
    {
      '@type': 'CollectionPage',
      '@id': `${pageUrl}/#webpage`,
      url: pageUrl,
      name: `Best Businesses in ${stateName} | Visibeen Directory`,
      description: `Find top-rated local businesses and services in ${stateName}. Browse ${cities?.length || 0} cities.`,
      isPartOf: { '@id': `${baseUrl}/#website` },
      about: {
        '@type': 'AdministrativeArea',
        name: stateName,
        containedInPlace: { '@type': 'Country', name: 'India' }
      },
      mainEntity: {
        '@type': 'ItemList',
        name: `Cities in ${stateName}`,
        numberOfItems: cities?.length || 0,
        itemListElement: (cities || []).slice(0, 20).map((city, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'City',
            name: city.name || city.slug,
            url: `${baseUrl}/in/${stateSlug}/${city.slug}`
          }
        }))
      }
    }
  ]);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export default function StatePage() {
  const { stateSlug } = useParams();
  const [cities, setCities] = useState([]);
  const [stateMeta, setStateMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const cityList = await fetchJson(`/api/v1/directory/states/${stateSlug}/cities`);
        setCities(cityList);
        setStateMeta({ slug: stateSlug });
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [stateSlug]);

  if (loading) return <div>Loading cities…</div>;
  if (error) return <div className="error">{error}</div>;

  const stateName = stateMeta?.name || stateSlug;
  const formattedStateName = stateName.charAt(0).toUpperCase() + stateName.slice(1).replace(/-/g, ' ');

  const seoData = generateCompleteSEO({
    title: `Best Businesses in ${formattedStateName}`,
    description: `Find top-rated local businesses and services in ${formattedStateName}. Browse ${cities?.length || 0} cities. Discover reviews, ratings and contact information for businesses across ${formattedStateName}.`,
    keywords: [
      `businesses in ${formattedStateName}`,
      `${formattedStateName} directory`,
      `local services ${formattedStateName}`,
      `${formattedStateName} business listings`,
      'find businesses',
      'local businesses India'
    ],
    url: `/in/${stateSlug}`,
    type: 'website'
  });

  return (
    <>
      <SEOHead
        title={seoData.title}
        description={seoData.meta.description}
        keywords={seoData.meta.keywords}
        canonical={seoData.link.canonical}
        robots={seoData.meta.robots}
        ogTitle={seoData.meta['og:title']}
        ogDescription={seoData.meta['og:description']}
        ogUrl={seoData.meta['og:url']}
        ogType={seoData.meta['og:type']}
        twitterTitle={seoData.meta['twitter:title']}
        twitterDescription={seoData.meta['twitter:description']}
      />

      <StateSchema stateSlug={stateSlug} stateName={stateName} cities={cities} />
      <CityGrid
        cities={cities}
        stateName={stateName}
        stateSlug={stateSlug}
      />
    </>
  );
}


